import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createABLineGeoJSON } from '../../../utils/ShapefileUtils';
import PropTypes from 'prop-types';

/**
 * ShapefileVisualizer - A React component for visualizing GeoJSON on a map
 *
 * @component
 */
const ShapefileVisualizer = ({
  fieldBoundaryGeoJSON,
  trialBoundaryGeoJSON,
  abLineCoordinates,
  width = '100%',
  height = '600px',
  brandColor = '#007f66',
  centerMap = true,
  className = '',
  tileLayer = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef({
    fieldBoundary: null,
    trialBoundary: null,
    abLine: null,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize the map
  useEffect(() => {
    if (!mapContainerRef.current) return;

    // Only create the map if it doesn't exist yet
    if (!mapInstanceRef.current) {
      const mapOptions = {
        zoomControl: true,
        maxZoom: 20,
        minZoom: 13,
      };

      // Create the map instance
      const map = L.map(mapContainerRef.current, mapOptions);

      // Add the base tile layer
      L.tileLayer(tileLayer, {
        attribution: 'Map tiles &copy; Esri',
        maxZoom: 22,
      }).addTo(map);

      // Store the map instance in the ref
      mapInstanceRef.current = map;
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [tileLayer]);

  // Process field boundary GeoJSON
  useEffect(() => {
    const loadFieldBoundary = async () => {
      if (!mapInstanceRef.current || !fieldBoundaryGeoJSON) return;

      try {
        setIsLoading(true);

        // Remove existing field boundary layer if it exists
        if (layersRef.current.fieldBoundary) {
          mapInstanceRef.current.removeLayer(layersRef.current.fieldBoundary);
          layersRef.current.fieldBoundary = null;
        }

        // Create the GeoJSON layer with styling
        const layer = L.geoJSON(fieldBoundaryGeoJSON, {
          style: {
            color: 'black',
            weight: 2,
            fillColor: brandColor,
            fillOpacity: 0.75,
          },
        }).addTo(mapInstanceRef.current);

        // Store the layer in the ref
        layersRef.current.fieldBoundary = layer;

        // Center the map on the field boundary if requested
        if (centerMap) {
          mapInstanceRef.current.fitBounds(layer.getBounds().pad(0.05));
        }

        setIsLoading(false);
      } catch (err) {
        setError('Failed to load field boundary');
        setIsLoading(false);
      }
    };

    loadFieldBoundary();
  }, [fieldBoundaryGeoJSON, brandColor, centerMap]);

  // Process trial boundary GeoJSON
  useEffect(() => {
    const loadTrialBoundary = async () => {
      if (!mapInstanceRef.current || !trialBoundaryGeoJSON) return;

      try {
        setIsLoading(true);

        // Remove existing trial boundary layer if it exists
        if (layersRef.current.trialBoundary) {
          mapInstanceRef.current.removeLayer(layersRef.current.trialBoundary);
          layersRef.current.trialBoundary = null;
        }

        // Create the GeoJSON layer with styling
        const layer = L.geoJSON(trialBoundaryGeoJSON, {
          style: {
            color: 'white',
            weight: 2,
            fillOpacity: 0,
          },
        }).addTo(mapInstanceRef.current);

        // Store the layer in the ref
        layersRef.current.trialBoundary = layer;

        setIsLoading(false);
      } catch (err) {
        setError('Failed to load trial boundary');
        setIsLoading(false);
      }
    };

    loadTrialBoundary();
  }, [trialBoundaryGeoJSON]);

  // Process AB line coordinates
  useEffect(() => {
    const loadABLine = () => {
      if (
        !mapInstanceRef.current ||
        !abLineCoordinates ||
        abLineCoordinates.length < 2
      )
        return;

      try {
        // Remove existing AB line layer if it exists
        if (layersRef.current.abLine) {
          mapInstanceRef.current.removeLayer(layersRef.current.abLine);
          layersRef.current.abLine = null;
        }

        // Create GeoJSON for the AB line
        const geojson = createABLineGeoJSON(abLineCoordinates);

        // Create the GeoJSON layer with styling
        const layer = L.geoJSON(geojson, {
          style: {
            color: 'red',
            weight: 3,
            opacity: 0.8,
          },
        }).addTo(mapInstanceRef.current);

        // Store the layer in the ref
        layersRef.current.abLine = layer;
      } catch (err) {
        setError(`Failed to load AB line: ${err.message}`);
      }
    };

    loadABLine();
  }, [abLineCoordinates]);

  return (
    <div className={`shapefile-visualizer ${className}`}>
      {isLoading && <div className="loading-indicator">Loading data...</div>}
      {error && <div className="error-message">{error}</div>}
      <div
        ref={mapContainerRef}
        style={{ width, height, position: 'relative' }}
        className="map-container"
      />
    </div>
  );
};

ShapefileVisualizer.propTypes = {
  /** GeoJSON for the field boundary */
  fieldBoundaryGeoJSON: PropTypes.object,
  /** GeoJSON for the trial boundary (optional) */
  trialBoundaryGeoJSON: PropTypes.object,
  /** Array of [longitude, latitude] coordinates for the AB line */
  abLineCoordinates: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  /** Width of the map container */
  width: PropTypes.string,
  /** Height of the map container */
  height: PropTypes.string,
  /** Brand color for filling the field boundary polygon */
  brandColor: PropTypes.string,
  /** Whether to center the map on the field boundary */
  centerMap: PropTypes.bool,
  /** Additional CSS class for the container */
  className: PropTypes.string,
  /** URL template for the map tiles */
  tileLayer: PropTypes.string,
};

export default ShapefileVisualizer;
